// Persistent left nav. Section labels are non-clickable; nav items
// are TanStack Router <Link> components, with an active-state tint
// driven by the current pathname.
//
// Below `md` the sidebar is hidden behind a hamburger toggle in the
// top bar — see AppShell for the open/close state plumbing. On
// desktop the sidebar is statically positioned in the layout grid.
//
// Bottom of the sidebar carries the signed-in user's email + a
// sign-out button (brief: replaces the per-page "Signed in as /
// Sign out" affordance that lived in each page's header before the
// shell vertical).

import { Link, useRouterState } from '@tanstack/react-router';
import { useEffect, useState, type ComponentType, type SVGProps } from 'react';
import { useAuth } from '../../lib/auth/auth-context';
import { supabase } from '../../lib/supabase';
import {
  publishExceptionsCount,
  refetchExceptionsCount,
  useExceptionsCount,
} from '../../lib/exceptions/count-store';
import {
  IconAlert,
  IconBuilding,
  IconCalendar,
  IconClipboard,
  IconDashboard,
  IconDownload,
  IconPackage,
  IconPill,
  IconRadar,
  IconRoute,
  IconTruck,
  IconUpload,
  IconUsers,
} from '../ui/Icon';

type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;

// Role visibility for a nav item. platform_admin always sees every
// item (LYN staff override) — that's handled at filter time and not
// repeated in the per-item list.
type VisibleTo = readonly ('pharmacy_admin' | 'pharmacy_staff' | 'dispatcher')[];

type NavItem = {
  label: string;
  icon: IconComponent;
  to:
    | '/'
    | '/dashboard'
    | '/exceptions'
    | '/orders'
    | '/orders/upload'
    | '/reports'
    | '/routes'
    | '/dispatch'
    | '/schedule'
    | '/drivers'
    | '/patients'
    | '/coverage-map'
    | '/audit'
    | '/admin/pharmacy';
  // Some destinations require search params; we pass them when
  // present so TanStack Router's typed `to` doesn't error.
  search?: Record<string, string>;
  // Which non-admin roles see this nav item. platform_admin is added
  // implicitly downstream. Missing means "no one outside platform_admin"
  // — useful for items we want to keep accessible only to LYN staff.
  visibleTo: VisibleTo;
};

type NavSection = {
  label: string;
  items: NavItem[];
};

// Section-grouped nav. Order within each section is authored for the
// pharmacy view (Dom's spec 2026-08-17). Role filtering strips items
// each user can't see, so dispatchers + platform_admin land on
// coherent subsets. Sections that end up empty after filtering are
// dropped in the render step to avoid orphan headings.
const NAV: NavSection[] = [
  {
    label: 'Operations',
    items: [
      // Dashboard — pharmacy home. Own route + KPI/trend content
      // (see _authenticated.dashboard.tsx). v0 preview ships with
      // placeholder numbers; aggregation RPC + real data land in a
      // follow-up PR.
      {
        label: 'Dashboard',
        to: '/dashboard',
        icon: IconDashboard,
        visibleTo: ['pharmacy_admin', 'pharmacy_staff'],
      },
      // Dispatcher home — CNC's morning workload planner. `/`
      // redirects here for dispatchers (see _authenticated.index.tsx).
      {
        label: 'Dispatch',
        to: '/dispatch',
        icon: IconRadar,
        visibleTo: ['dispatcher'],
      },
      // Routes — retracted from pharmacy roles 2026-07-10; the
      // surface stays wired so a dispatcher (or platform_admin) can
      // reach /routes/:id via drill-down from /dispatch, but it's no
      // longer a pharmacy sidebar item. Add back only when there's a
      // pharmacy-shaped Routes view.
      {
        label: 'Routes',
        to: '/routes',
        icon: IconRoute,
        search: { date: 'today', status: 'all', driver: 'all' },
        visibleTo: [],
      },
      // Orders sits above Follow-ups per user request 2026-08-18 —
      // Orders is the daily working surface, Follow-ups is the
      // drill-into for problems, so the surface with the higher
      // daily traffic sits first.
      {
        label: 'Orders',
        to: '/orders',
        icon: IconPackage,
        search: { status: 'all' },
        visibleTo: ['pharmacy_admin', 'pharmacy_staff', 'dispatcher'],
      },
      // Follow-ups (route path /exceptions) — pharmacist's decision
      // surface for orders needing human attention. Renamed from
      // "Exceptions" for a softer, less alarm-state read. Route +
      // RPCs + DB enums (`list_exceptions` / `order_exception_type`)
      // keep the original names so audit-log breadcrumbs + external
      // references stay stable. See PR #111 for the schema and PR
      // #113 for the UI.
      {
        label: 'Follow-ups',
        to: '/exceptions',
        icon: IconAlert,
        visibleTo: ['pharmacy_admin', 'pharmacy_staff'],
      },
    ],
  },
  {
    label: 'Records',
    items: [
      // Patients + Audit log are pharmacy-side compliance surfaces.
      // Dispatchers (CNC ops) don't have a need-to-know for PHI.
      {
        label: 'Patients',
        to: '/patients',
        icon: IconUsers,
        visibleTo: ['pharmacy_admin', 'pharmacy_staff'],
      },
      // Coverage Map — geographic view of every delivery in the
      // pharmacy's visible set. Clustering by default, aggregation
      // buckets by city / ZIP, filters for date range / pharmacy /
      // cold-chain / driver / day-of-week. Records-side surface
      // because it's read-only compliance/analytics, not dispatch.
      {
        label: 'Coverage Map',
        to: '/coverage-map',
        icon: IconRadar,
        visibleTo: ['pharmacy_admin', 'pharmacy_staff'],
      },
      // Reports — bulk audit manifest export for state-board
      // queries. See PR #115.
      {
        label: 'Reports',
        to: '/reports',
        icon: IconDownload,
        visibleTo: ['pharmacy_admin', 'pharmacy_staff'],
      },
      {
        label: 'Audit log',
        to: '/audit',
        icon: IconClipboard,
        visibleTo: ['pharmacy_admin', 'pharmacy_staff'],
      },
    ],
  },
  {
    label: 'Admin',
    items: [
      // Driver roster: dispatchers manage enrollment + edits.
      // Pharmacies see the driver name on each order + audit PDF
      // automatically — they don't hire or manage the roster, so
      // retracted from pharmacy roles 2026-07-10.
      {
        label: 'Drivers',
        to: '/drivers',
        icon: IconTruck,
        search: { status: 'active' },
        visibleTo: ['dispatcher'],
      },
      // Driver scheduling — pairs with the Drivers roster. Workforce
      // planning is the delivery operator's problem, not the
      // pharmacy's.
      {
        label: 'Schedule',
        to: '/schedule',
        icon: IconCalendar,
        visibleTo: ['dispatcher'],
      },
      {
        label: 'Upload orders',
        to: '/orders/upload',
        icon: IconUpload,
        visibleTo: ['pharmacy_admin', 'pharmacy_staff'],
      },
      {
        label: 'Pharmacy',
        to: '/admin/pharmacy',
        icon: IconBuilding,
        visibleTo: ['pharmacy_admin'],
      },
    ],
  },
];

// Returns today's date in ISO YYYY-MM-DD using local time. Recomputed
// once when the sidebar mounts; navigating to "Routes" from the
// sidebar always lands on today (the route page's filter row can
// shift to other dates).
function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isActive(itemTo: string, pathname: string): boolean {
  // Exact match for the dashboard root; prefix match for everything
  // else so `/routes/636e8ab2-...` highlights the "Routes" item.
  if (itemTo === '/') return pathname === '/';
  return pathname === itemTo || pathname.startsWith(`${itemTo}/`);
}

type Props = {
  onNavigate?: () => void;
};

export function Sidebar({ onNavigate }: Props): JSX.Element {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { user, userType, signOut } = useAuth();
  const today = todayIso();

  // Dev-only identity row: surfaces the caller's user_type +
  // tenant_role so designers (Dom) don't have to inspect the JWT or
  // run a SQL query while building role-aware surfaces. JWT carries
  // user_type but not tenant_role (PR #7 narrowed claims), so we
  // fetch tenant_role from public.users. Gated by import.meta.env.DEV
  // — tree-shaken out of production builds.
  const [tenantRole, setTenantRole] = useState<string | null>(null);
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!user) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('users')
        .select('tenant_role')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled) return;
      setTenantRole(data?.tenant_role ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Exceptions count badge — fetch once when the caller becomes a
  // pharmacy role (dispatchers don't see the item, no need to poll),
  // then refetch on window focus so a pharmacist returning to the
  // tab sees a fresh count. The /exceptions page also publishes
  // updates via refetchExceptionsCount() after every successful
  // decide — that's the primary "count went down" signal; the focus
  // listener catches count *increases* driven by upstream state
  // (new failures, POD gaps aging past the threshold).
  const canSeeExceptions =
    userType === 'pharmacy_admin' ||
    userType === 'pharmacy_staff' ||
    userType === 'platform_admin';
  useEffect(() => {
    if (!canSeeExceptions) return;
    // DEV-only demo mode — `?demo=1` anywhere in the app publishes a
    // fixed mock count so the badge is coherent with the /exceptions
    // ?demo=1 fixture. Tree-shaken from prod via import.meta.env.DEV.
    const demoMode =
      import.meta.env.DEV &&
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('demo') === '1';
    if (demoMode) {
      // Keep in sync with DEMO_ROWS in _authenticated.exceptions.tsx.
      publishExceptionsCount(7);
      return;
    }
    void refetchExceptionsCount();
    const onFocus = (): void => {
      void refetchExceptionsCount();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [canSeeExceptions]);
  const exceptionsCount = useExceptionsCount();

  const handleSignOut = async (): Promise<void> => {
    await signOut();
    // The auth guard will redirect on the next route render; we don't
    // navigate explicitly here because the sign-out cascade may differ
    // by entry point.
  };

  // Filter nav to what the current role can see. platform_admin is
  // the LYN-staff override and sees every item regardless of the
  // per-item visibleTo list. Sections that end up empty after
  // filtering are dropped so we don't render an orphan section
  // header (e.g. "Records" with no children).
  const visibleNav = NAV.map((section) => ({
    label: section.label,
    items: section.items.filter((item) => {
      if (userType === 'platform_admin') return true;
      if (userType === null) return false;
      return (item.visibleTo as readonly string[]).includes(userType);
    }),
  })).filter((section) => section.items.length > 0);

  return (
    <aside
      aria-label="Primary navigation"
      className="flex h-full w-48 shrink-0 flex-col border-r border-border bg-bg"
    >
      <div className="flex items-center gap-2 px-3 py-3">
        <span
          className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-white"
          aria-hidden="true"
        >
          <IconPill size={14} />
        </span>
        <p className="text-sm font-semibold tracking-wide text-ink">LYN Rx</p>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-2">
        {visibleNav.map((section) => (
          <div key={section.label} className="mb-3">
            <p className="px-3 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-3">
              {section.label}
            </p>
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const active = isActive(item.to, pathname);
                const className = active
                  ? 'flex items-center gap-2.5 rounded-md border-l-2 border-primary bg-bg-soft px-3 py-1.5 text-xs font-semibold text-ink'
                  : 'flex items-center gap-2.5 rounded-md border-l-2 border-transparent px-3 py-1.5 text-xs font-medium text-ink-2 hover:bg-bg-soft hover:text-ink';
                const iconClassName = active ? 'text-primary' : 'text-ink-3';
                // Build a stable search object for the typed Link.
                // Today's date is injected for /routes so the sidebar
                // always lands on the today view.
                let search: Record<string, string> | undefined = item.search;
                if (item.to === '/routes') {
                  search = {
                    date: today,
                    status: 'all',
                    driver: 'all',
                  };
                }
                const Icon = item.icon;
                return (
                  <li key={item.to + item.label}>
                    <Link
                      to={item.to}
                      search={search as never}
                      className={className}
                      onClick={onNavigate}
                    >
                      <Icon size={16} className={iconClassName} />
                      <span>{item.label}</span>
                      {item.to === '/exceptions' &&
                      exceptionsCount !== null &&
                      exceptionsCount > 0 ? (
                        <span
                          aria-label={`${exceptionsCount} pending`}
                          className={
                            'ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold ' +
                            (active
                              ? 'bg-primary text-white'
                              : 'bg-red-100 text-red-700')
                          }
                        >
                          {exceptionsCount}
                        </span>
                      ) : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-border px-3 py-2">
        <p className="truncate text-[11px] text-ink-2" title={user?.email ?? ''}>
          {user?.email ?? 'Signed in'}
        </p>
        <button
          type="button"
          onClick={() => {
            void handleSignOut();
          }}
          className="text-[11px] text-primary hover:underline"
        >
          Sign out
        </button>
        {import.meta.env.DEV ? (
          <p
            className="mt-1 truncate font-mono text-[9px] text-ink-3"
            title={`user_type=${userType ?? 'null'} · tenant_role=${tenantRole ?? 'null'}`}
          >
            <span className="rounded-sm bg-bg-soft px-1 py-0.5 text-ink-2">DEV</span>{' '}
            {userType ?? 'null'} · {tenantRole ?? 'null'}
          </p>
        ) : null}
      </div>
    </aside>
  );
}
